
import React, { useState } from 'react';

const VATReturn: React.FC = () => {
    const [connected, setConnected] = useState(false);
    const [vrn, setVrn] = useState('');
    const [obligations, setObligations] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    const handleConnect = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${process.env.REACT_APP_API_URL}/api/financials/hmrc/auth-url`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success && data.url) {
                window.location.href = data.url; // Redirect to HMRC
            }
        } catch (error) {
            console.error('Error connecting to HMRC:', error);
        }
    };

    const fetchObligations = async () => {
        if (!vrn) return alert('Please enter VRN');
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            // Hardcoded dates for MVP/Sandbox
            const from = '2023-01-01';
            const to = '2023-12-31';

            const res = await fetch(`${process.env.REACT_APP_API_URL}/api/financials/hmrc/obligations?vrn=${vrn}&from=${from}&to=${to}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success) {
                setObligations(data.data.obligations || []);
                setConnected(true);
            } else {
                alert('Failed to fetch obligations: ' + data.message);
            }
        } catch (error) {
            console.error('Error fetching obligations:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-medium text-gray-900">MTD VAT Return</h3>
                <div className="flex items-center space-x-2">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${connected ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                        {connected ? 'Connected to HMRC' : 'Not Connected'}
                    </span>
                    {!connected && (
                        <button
                            onClick={handleConnect}
                            className="text-sm text-emerald-600 hover:text-emerald-500 font-medium"
                        >
                            Connect Now
                        </button>
                    )}
                </div>
            </div>

            <div className="space-y-6">
                <div>
                    <label className="block text-sm font-medium text-gray-700">VAT Registration Number (VRN)</label>
                    <div className="mt-1 flex rounded-md shadow-sm">
                        <input
                            type="text"
                            value={vrn}
                            onChange={(e) => setVrn(e.target.value)}
                            className="flex-1 min-w-0 block w-full px-3 py-2 rounded-md border border-gray-300 focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm"
                            placeholder="123456789"
                        />
                        <button
                            onClick={fetchObligations}
                            disabled={loading}
                            className="ml-3 inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50"
                        >
                            {loading ? 'Fetching...' : 'Fetch Obligations'}
                        </button>
                    </div>
                    <p className="mt-2 text-sm text-gray-500">Enter your 9-digit VRN to check obligations.</p>
                </div>

                {obligations.length > 0 && (
                    <div className="mt-6">
                        <h4 className="text-sm font-medium text-gray-900 mb-4">Open Obligations</h4>
                        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                            <table className="min-w-full divide-y divide-gray-300">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Period</th>
                                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Start</th>
                                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">End</th>
                                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Due Date</th>
                                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Status</th>
                                        <th className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                                            <span className="sr-only">Action</span>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 bg-white">
                                    {obligations.map((ob, idx) => (
                                        <tr key={idx}>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 font-medium">{ob.periodKey}</td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{ob.start}</td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{ob.end}</td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{ob.due}</td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                <span className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${ob.status === 'O' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>
                                                    {ob.status === 'O' ? 'Open' : 'Fulfilled'}
                                                </span>
                                            </td>
                                            <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                                                {ob.status === 'O' && (
                                                    <button className="text-emerald-600 hover:text-emerald-900">Submit Return<span className="sr-only">, {ob.periodKey}</span></button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default VATReturn;
